import { StatusBar, Style } from '@capacitor/status-bar';
import { isPlatform } from '@ionic/core';
import { useEffect, useState } from 'react';
import {
  UserPreferences,
  useGetUserPreference,
} from '../api/hooks/preference.hooks';
import { useGetForcedColorScheme } from '../components/branding/branding.hooks';
import { Theme } from '../config/branding';

const useDarkMode = (): boolean => {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');

  const [isDarkMode, setDarkMode] = useState<boolean>(prefersDark.matches);

  if (prefersDark && typeof prefersDark.addEventListener === 'function') {
    prefersDark.addEventListener('change', (mediaQuery) =>
      setDarkMode(mediaQuery.matches)
    );
  }

  return isDarkMode;
};

const setTheme = (theme: Theme, forcedColorScheme: Theme | undefined) => {
  let isDark = false;
  if (forcedColorScheme) {
    isDark = forcedColorScheme === Theme.DARK;
  } else {
    isDark = theme === Theme.DARK;
  }
  // document.body.classList.toggle(Theme.DARK, isDark);
  document.documentElement.classList.toggle('ion-palette-dark', isDark);
  if (isPlatform('capacitor')) {
    StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  }
};

const useSyncThemeWithoutPreference = () => {
  const systemDarkMode = useDarkMode();
  const forcedColorScheme = useGetForcedColorScheme();

  useEffect(() => {
    setTheme(systemDarkMode ? Theme.DARK : Theme.LIGHT, forcedColorScheme);
  });
};

const useSyncTheme = () => {
  const themePreference = useGetUserPreference<Theme>(
    UserPreferences.USER_THEME,
    {
      fetchPolicy: 'cache-only',
    }
  );

  const systemDarkMode = useDarkMode();
  const forcedColorScheme = useGetForcedColorScheme();

  useEffect(() => {
    if (themePreference?.value !== undefined) {
      if (themePreference.value === Theme.SYSTEM) {
        setTheme(systemDarkMode ? Theme.DARK : Theme.LIGHT, forcedColorScheme);
      } else {
        setTheme(themePreference.value, forcedColorScheme);
      }
    } else {
      setTheme(systemDarkMode ? Theme.DARK : Theme.LIGHT, forcedColorScheme);
    }
  }, [systemDarkMode, themePreference, forcedColorScheme]);
};

export { useDarkMode, useSyncTheme, useSyncThemeWithoutPreference };
